import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import ToggleCheck from '@/assets/icons/toggleCheck';
import ToggleCross from '@/assets/icons/toggleCross';
import { Switch } from '@/components/Switch';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components/Row';
import { ds } from '@/components/lib/design.system';
import { useBonusRemovesPromoSheet } from '@/hooks/use-bonus-removes-promo-sheet';
import { useWallet } from '@/hooks/use-wallet';
import { designSystem } from '@/styles/styles';
import { toSocialCurrency } from '@/utils/numeric/currency';

import { useSbkBetSlipStore } from '../hooks/use-sbk-betslip-store';

const BonusToggle = ({ betId }: { betId: string }) => {
    const { t } = useTranslation('betslip_sbk');
    const toggleUseBetrBucks = useSbkBetSlipStore(state => state.actions.toggleUseBetrBucks);
    const enableBetrBucksAndRemovePromo = useSbkBetSlipStore(state => state.actions.enableBetrBucksAndRemovePromo);
    const isEnabled = useSbkBetSlipStore(state => !!state.isBetrBucksEnabled[betId]);
    const hasSelectedPromo = useSbkBetSlipStore(state => !!state.selectedPromos[betId]);
    const showBonusRemovesPromoSheet = useBonusRemovesPromoSheet();

    const handleOnPress = useCallback(() => {
        if (!isEnabled && hasSelectedPromo) {
            showBonusRemovesPromoSheet({
                message: t('playing_with_bonus_will_remove_promo'),
                confirmLabel: t('play_with_bonus'),
                onConfirm: () => enableBetrBucksAndRemovePromo(betId),
            });
            return;
        }
        toggleUseBetrBucks(betId);
    }, [
        isEnabled,
        hasSelectedPromo,
        showBonusRemovesPromoSheet,
        enableBetrBucksAndRemovePromo,
        toggleUseBetrBucks,
        betId,
        t,
    ]);

    return (
        <Switch
            val={isEnabled}
            handleOnPress={handleOnPress}
            onIcon={<ToggleCheck />}
            offIcon={<ToggleCross />}
            activeTrackColor={ds.colorValue.background.brand.emphasis}
            inactiveTrackColor={designSystem.colors.neutral600}
        />
    );
};

export const BonusToggleRow = ({ betId }: { betId: string }) => {
    const { t } = useTranslation('betslip_sbk');
    const { betrBucks } = useWallet();
    return (
        <Row alignItems="center" justifyContent="space-between" mt="s16">
            <Row>
                <Text variant="bodyMedium" color={ds.colorToken.text.primary}>
                    {t('use_bonus')}:{' '}
                </Text>
                <Text variant="bodyMedium" color={ds.colorToken.text.brand}>
                    {toSocialCurrency(betrBucks)}
                </Text>
            </Row>
            <BonusToggle betId={betId} />
        </Row>
    );
};
